/**
 * Tracing records the state changes that *cause* template re-renders — parameter changes, datasource
 * refreshes, frame-data updates, canvas-state updates and template pushes — so they can be correlated
 * with the render passes captured by the profiling slice.  A `Trace` carries both a wall-clock `timestamp` (for display
 * in the Event Log) and a `performance.now()`-based `perfTime` (to line up against a profiling cycle's
 * `startTime`, which is also `performance.now()`-based).
 */
export interface TracingState {
    // Persisted across sessions (localStorage); gates whether `addTraceEvent` records anything.
    enabled: boolean;
    // Recorded traces, oldest -> newest.  Trimmed to the last `maxTraces` (ring buffer).
    traces: Trace[];
    maxTraces: number;
}

interface ParameterValueChangedEvent {
    kind: "ParameterValueChanged";
    name: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

interface DatasourceRefreshedEvent {
    kind: "DatasourceRefreshed";
    name: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

interface DatasourceErrorEvent {
    kind: "DatasourceErrorOccurred";
    name: string;
    error: any;
    source: TraceSource;
}

interface FrameDataChangedEvent {
    kind: "FrameDataChanged";
    name?: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

interface CanvasStateChangedEvent {
    kind: "CanvasStateChanged";
    name?: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

interface DatasourcesInFlightChangedEvent {
    kind: "DatasourcesInFlightChanged";
    name?: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

// A template's jsx/css was pushed (Dev Tools window or VS Code extension); old/new hold the
// template's version tag (e.g. "v3" -> "v4") rather than the full JSX content.
interface TemplateUpdatedEvent {
    kind: "TemplateUpdated";
    name: string;
    oldValue?: any;
    newValue: any;
    source: TraceSource;
}

interface DataSourceBindingTraceSource {
    kind: "DatasourceBinding";
    datasourceName: string;
}

interface InputControlChangeTraceSource {
    kind: "InputControlChange";
}

interface ManualUpdateTraceSource {
    kind: "ManualUpdate";
}

interface InitialLoadTraceSource {
    kind: "InitialLoad";
}

interface ParameterValueChangedTraceSource {
    kind: "ParameterValueChanged";
}

interface PageNavigationTraceSource {
    kind: "PageNavigation";
}

interface UpdateParameterValueCallTraceSource {
    kind: "UpdateParameterValueCall";
}

interface RefreshDatasourceByNameCallTraceSource {
    kind: "RefreshDatasourceByNameCall";
}

interface DatasourceRequestTraceSource {
    kind: "DatasourceRequest";
}

interface DatasourceResponseTraceSource {
    kind: "DatasourceResponse";
}

interface DevToolsTraceSource {
    kind: "DevTools";
}

interface VsCodeExtensionTraceSource {
    kind: "VsCodeExtension";
}

export type TraceSource =
    | DataSourceBindingTraceSource
    | InputControlChangeTraceSource
    | ManualUpdateTraceSource
    | InitialLoadTraceSource
    | ParameterValueChangedTraceSource
    | PageNavigationTraceSource
    | UpdateParameterValueCallTraceSource
    | RefreshDatasourceByNameCallTraceSource
    | DatasourceRequestTraceSource
    | DatasourceResponseTraceSource
    | DevToolsTraceSource
    | VsCodeExtensionTraceSource;

export type TraceEvent =
    | ParameterValueChangedEvent
    | DatasourceRefreshedEvent
    | DatasourceErrorEvent
    | FrameDataChangedEvent
    | CanvasStateChangedEvent
    | DatasourcesInFlightChangedEvent
    | TemplateUpdatedEvent;

export type Trace = {
    timestamp: Date;
    // `performance.now()` value when the trace was recorded; correlated against profiling cycle start times.
    perfTime: number;
    event: TraceEvent;
};

export const ADD_TRACE_EVENT = "ADD_TRACE_EVENT";
export const TRACING_SET_ENABLED = "TRACING_SET_ENABLED";
export const TRACING_CLEAR = "TRACING_CLEAR";

export interface AddTraceAction {
    type: typeof ADD_TRACE_EVENT;
    trace: Trace;
}

export interface SetTracingEnabledAction {
    type: typeof TRACING_SET_ENABLED;
    enabled: boolean;
}

export interface ClearTracingAction {
    type: typeof TRACING_CLEAR;
}

export type TracingActionTypes = AddTraceAction | SetTracingEnabledAction | ClearTracingAction;
